import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";
import { LinkOutlined } from "@ant-design/icons";

export const LinkForm = ({ type }) => {
  /** Dealer login: no OEM/Dealer pickers; scope from session. */
  const hideOemDealerForDealerAccount = hasRole(7, 8);
  const [showModal, setShowModal] = useState(false);
  const [selectedOemNo, setSelectedOemNo] = useState(null);
  const [selectedDealerNo, setSelectedDealerNo] = useState(null);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = id && `${process.env.REACT_APP_API_URL}/api/links/${id}/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/links/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/links/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/links/${id}/`;
  const accountOptionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;

  const { data, loading, refetch } = useGet(url);
  const { data: accountOptionsData } = useGet(accountOptionUrl);

  const resolveOemNo = useCallback(
    (value) => {
      if (value == null || value === "") return null;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const oemOption = accountOptionsData?.data?.oems?.find(
        (oem) => String(oem?.name) === String(value),
      );
      return oemOption?.value ?? null;
    },
    [accountOptionsData],
  );

  const resolvedOemNo =
    resolveOemNo(selectedOemNo) ??
    resolveOemNo(data?.data?.OemNo) ??
    (hideOemDealerForDealerAccount ? resolveOemNo(getOemNo()) : null);

  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const { data: dealerOptionsData } = useGet(dealerOptionsUrl);

  const getSessionDealerNoNum = () => {
    const d = getDealerNo();
    if (d == null || d === "") return null;
    const n = Number(d);
    return Number.isFinite(n) ? n : null;
  };

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);

  const modifiedOems = useMemo(
    () => getModifiedResponse(accountOptionsData, "oems"),
    [accountOptionsData],
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptionsData, null),
    [dealerOptionsData],
  );

  const onFinish = async (values) => {
    const payload = {
      ...values,
      ...(hideOemDealerForDealerAccount
        ? {
          OemNo: getOemNo(),
          DealerNo: getDealerNo(),
        }
        : {}),
    };
    if (!hideOemDealerForDealerAccount) {
      // OemNo is UI-only cascade helper; only DealerNo goes to API
      delete payload.OemNo;
    }

    try {
      if (id) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  useEffect(() => {
    if (!id || loading) return;
    try {
      form.resetFields();
      if (data?.data) {
        form.setFieldsValue({
          LinkName: data?.data?.LinkName ? String(data.data.LinkName) : "",
          LinkDesc: data?.data?.LinkDesc ? String(data.data.LinkDesc) : "",
          LinkUrl: data?.data?.LinkUrl ? String(data.data.LinkUrl) : "",
          OemNo:
            data?.data?.OemNo || data?.data?.OemNo === 0
              ? Number(data.data.OemNo)
              : undefined,
          DealerNo:
            data?.data?.DealerNo || data?.data?.DealerNo === 0
              ? Number(data.data.DealerNo)
              : undefined,
        });
        setSelectedOemNo(data?.data?.OemNo || null);
        setSelectedDealerNo(data?.data?.DealerNo || null);
      }
    } catch (error) {
      if (!loading) {
        openErrorNotification("Error fetching data!");
      }
    }
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (!hideOemDealerForDealerAccount || id) return;
    setSelectedOemNo(resolveOemNo(getOemNo()));
    setSelectedDealerNo(getSessionDealerNoNum());
  }, [hideOemDealerForDealerAccount, id, resolveOemNo]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData, postLoading]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/v1-rmc");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "V1 RMC",
      href: "/v1-rmc",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/v1-rmc/info/${id}`
          : `/v1-rmc/edit/${id}`
        : "/v1-rmc/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
        {
          label: "Edit",
          icon: <EditOutlined />,
          onClick: () => {
            navigate(`/v1-rmc/edit/${id}`);
          },
        },
        {
          label: "Delete",
          icon: <DeleteOutlined />,
          onClick: () => {
            setShowModal(true);
          },
        },
        {
          label: "List",
          icon: <UnorderedListOutlined />,
          onClick: () => {
            navigate(-1);
          },
        },
      ]
      : [
        {
          label: "Apply",
          icon: <CheckCircleOutlined />,
          onClick: () => form.submit(),
        },
        {
          label: "Cancel",
          icon: <CloseOutlined />,
          onClick: onReset,
        },
      ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <LinkOutlined className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                V1 RMC
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Name"
                    name="LinkName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Description"
                    name="LinkDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="V1 RMC URL"
                    name="LinkUrl"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                {!hideOemDealerForDealerAccount ? (
                  <>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                      <CustomSelect
                        label="OEM"
                        name="OemNo"
                        options={modifiedOems}
                        disabled={type === "info"}
                        required
                        placeholder="Please select"
                        onChange={(value) => {
                          setSelectedOemNo(value || null);
                          setSelectedDealerNo(null);
                          form.setFieldsValue({
                            OemNo: value || undefined,
                            DealerNo: undefined,
                          });
                        }}
                        loading={loading}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                      <CustomSelect
                        label="Dealer"
                        name="DealerNo"
                        options={modifiedDealers}
                        disabled={
                          type === "info" ||
                          !(selectedOemNo || form.getFieldValue("OemNo"))
                        }
                        required
                        placeholder="Please select"
                        onChange={(value) => {
                          setSelectedDealerNo(value || null);
                          form.setFieldsValue({
                            DealerNo: value || undefined,
                          });
                        }}
                        formItemProps={{
                          rules: [
                            { required: true, message: "Dealer is required" },
                          ],
                        }}
                        loading={loading}
                      />
                    </Col>
                  </>
                ) : null}
              </Row>
            </div>
          </div>
        </div>
      </Form>

      {type !== "info" ? (
        <p className="mt-4 mb-0 text-customColor font-bold font-['Poppins'] text-base">
          You can click copy button at V1 and paste it to V1 RMC URL
        </p>
      ) : null}

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};